import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface Backorder {
  id: string;
  orderId: string;
  orderNumber: string;
  companyName: string;
  contactName: string;
  sku: string;
  itemName: string;
  quantity: number;
  status: 'pending' | 'fulfilled' | 'cancelled';
  fulfilledAt: string | null;
  fulfilledByName: string | null;
  queuePosition: number | null;
  createdAt: string;
}

interface Stats {
  pendingCount: number;
  fulfilledThisWeek: number;
  totalFulfilled: number;
  totalCancelled: number;
}

export default function BackordersPage() {
  const [backorders, setBackorders] = useState<Backorder[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      params.set('limit', '100');

      const [backorderData, statsData] = await Promise.all([
        api.get<{ items: Backorder[]; pagination: any }>(`/backorders?${params.toString()}`),
        api.get<Stats>('/backorders/stats'),
      ]);

      setBackorders(backorderData.items || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load backorders:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFulfill = async (id: string) => {
    if (!confirm('Mark this back-order as fulfilled?')) return;
    try {
      setActionLoading(id);
      await api.patch(`/backorders/${id}/fulfill`, {});
      await loadData();
    } catch (err) {
      console.error('Failed to fulfill backorder:', err);
      alert('Failed to fulfill back-order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id: string) => {
    if (!confirm('Cancel this back-order?')) return;
    try {
      setActionLoading(id);
      await api.patch(`/backorders/${id}/cancel`, {});
      await loadData();
    } catch (err) {
      console.error('Failed to cancel backorder:', err);
      alert('Failed to cancel back-order');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            Back-Order Queue
            {stats && stats.pendingCount > 0 && (
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                {stats.pendingCount} pending
              </span>
            )}
          </h1>
          <p className="text-gray-500 mt-1">
            Manage dealer back-orders for out-of-stock items. Processed first-come, first-served.
          </p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-orange-600">{stats.pendingCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Fulfilled This Week</p>
            <p className="text-2xl font-bold text-green-600">{stats.fulfilledThisWeek}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Fulfilled</p>
            <p className="text-2xl font-bold text-gray-700">{stats.totalFulfilled}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Cancelled</p>
            <p className="text-2xl font-bold text-gray-400">{stats.totalCancelled}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { value: 'pending', label: 'Pending' },
            { value: 'fulfilled', label: 'Fulfilled' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: '', label: 'All' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === opt.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search order #, company, SKU..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading back-orders...</div>
        ) : backorders.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-500 font-medium">No back-orders found</p>
            <p className="text-gray-400 text-sm mt-1">
              {statusFilter === 'pending' ? 'All caught up! No pending back-orders in the queue.' : 'No back-orders match the current filters.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Item</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ordered</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {backorders.map((bo, idx) => (
                <tr key={bo.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                    {bo.queuePosition || (idx + 1)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {bo.orderNumber || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div>{bo.companyName}</div>
                    {bo.contactName && bo.contactName !== bo.companyName && (
                      <div className="text-xs text-gray-400">{bo.contactName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{bo.sku}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{bo.itemName}</td>
                  <td className="px-4 py-3 text-sm text-center font-medium">{bo.quantity}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(bo.createdAt)}</td>
                  <td className="px-4 py-3">
                    {bo.status === 'pending' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        Pending
                      </span>
                    )}
                    {bo.status === 'fulfilled' && (
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Fulfilled
                        </span>
                        {bo.fulfilledAt && (
                          <div className="text-xs text-gray-400 mt-0.5">{formatDateTime(bo.fulfilledAt)}</div>
                        )}
                        {bo.fulfilledByName && (
                          <div className="text-xs text-gray-400">by {bo.fulfilledByName}</div>
                        )}
                      </div>
                    )}
                    {bo.status === 'cancelled' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        Cancelled
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {bo.status === 'pending' && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleFulfill(bo.id)}
                          disabled={actionLoading === bo.id}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === bo.id ? '...' : 'Fulfill'}
                        </button>
                        <button
                          onClick={() => handleCancel(bo.id)}
                          disabled={actionLoading === bo.id}
                          className="px-3 py-1 text-sm bg-white text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
